<script lang="ts">
  import { fileStore, activeTab, canUndoActiveTab, canRedoActiveTab } from '$lib/stores/fileStore'
  import { get } from 'svelte/store'
  import { onMount, onDestroy } from 'svelte'
  import ThemeToggleLogo from './ThemeToggleLogo.svelte'
  import { themeToggleService, type ThemeMode } from '$lib/services/ThemeToggleService'
  import { settingsStore, settingsActions } from '$lib/stores/settings'
  import { AI_MODELS } from '$lib/config/aiModels.config'
  import type { AIModelInfo } from '$lib/types/ai'
  import { systemPromptStore } from '$lib/stores/systemPrompt'
  import { postingPromptStore } from '$lib/stores/postingPrompt'
  import { unifiedPromptStore } from '$lib/stores/unifiedPrompt'
  import { demoTracker } from '$lib/stores/demoStore'

  const isDev = import.meta.env.DEV
  const isDemo = demoTracker.isDemoMode()
  
  // Import lucide icons for editing tools
  import {
    Undo2,
    Redo2,
    Scissors,
    Copy,
    ClipboardPaste,
    Trash2,
    Settings,
    MessageSquare,
    PenLine,
    RefreshCw,
    Maximize2,
    Minimize2,
    ListFilter,
    Bot,
    SlidersHorizontal,
    FileDown,
    Maximize,
    Minimize,
    ExternalLink
  } from 'lucide-svelte'
  
  // Import AI provider icons
  import openaiIcon from '$lib/../assets/ai-model-icons/openai.png'
  import claudeIcon from '$lib/../assets/ai-model-icons/claude.png'
  import googleIcon from '$lib/../assets/ai-model-icons/google.png'
  import openrouterIcon from '$lib/../assets/ai-model-icons/openrouter.png'
  import upstageIcon from '$lib/../assets/ai-model-icons/upstage.png'
  
  interface Props {
    editorText: string
    onClear: () => void
    onPaste: () => void
    onCopy: () => void
    onCut: () => void
    onOpenSettings: () => void
    onParaphrase?: () => void
    onContinue?: () => void
    onCritic?: () => void
    onExpand?: () => void
    onShrink?: () => void
    onSummarize?: () => void
    onPrompt?: () => void
    onUnifiedPrompt?: () => void
    onExportPDF?: () => void
    onToggleFullscreen?: () => void
    isFullscreen?: boolean
    hasSelection?: boolean
    isInSplitView?: boolean
    hasGrammarCheckErrors?: boolean
    isFileOpen?: boolean
  }

  let { 
    editorText, 
    onClear, 
    onPaste, 
    onCopy, 
    onCut, 
    onOpenSettings,
    onParaphrase = () => {},
    onContinue = () => {},
    onCritic = () => {},
    onExpand = () => {},
    onShrink = () => {},
    onSummarize = () => {},
    onPrompt = () => {},
    onUnifiedPrompt = () => {},
    onExportPDF = () => {},
    onToggleFullscreen = () => {},
    isFullscreen = false,
    hasSelection = false,
    isInSplitView = false,
    hasGrammarCheckErrors = false,
    isFileOpen = false
  }: Props = $props()
  
  // Undo/Redo state - reactive from stores
  const canUndo = $derived(get(canUndoActiveTab))
  const canRedo = $derived(get(canRedoActiveTab))
  
  // Subscribe to store changes
  let unsubscribeCanUndo: (() => void) | null = null
  let unsubscribeCanRedo: (() => void) | null = null
  let canUndoState = $state(false)
  let canRedoState = $state(false)
  
  $effect(() => {
    // Subscribe to canUndo changes
    unsubscribeCanUndo = canUndoActiveTab.subscribe(value => {
      canUndoState = value
    })
    
    // Subscribe to canRedo changes
    unsubscribeCanRedo = canRedoActiveTab.subscribe(value => {
      canRedoState = value
    })
    
    return () => {
      if (unsubscribeCanUndo) unsubscribeCanUndo()
      if (unsubscribeCanRedo) unsubscribeCanRedo()
    }
  })
  
  // Theme state
  let currentTheme = $state<ThemeMode>('auto')
  
  // AI Model state - reactive to settings store
  let showModelDropdown = $state(false)
  let modelDropdownRef = $state<HTMLDivElement | null>(null)
  let modelButtonRef = $state<HTMLButtonElement | null>(null)
  let dropdownPosition = $state({ left: 0, top: 0 })
  
  // Get settings reactively using store subscription
  const settings = $derived($settingsStore)
  
  // Get posting prompt state
  const postingPromptState = $derived($postingPromptStore)
  
  // Get selected model - check posting-specific model first, then fall back to global
  // Default to gpt-oss-120b when nothing is set
  const selectedModelId = $derived.by(() => {
    const modelId = postingPromptState.data?.modelId || settings?.ai?.selectedModel || 'openai/gpt-oss-120b'
    // Debug: Uncomment to see model selection details
    // console.log('[Toolbar] Derived selectedModelId:', modelId, {
    //   postingModelId: postingPromptState.data?.modelId,
    //   settingsModelId: settings?.ai?.selectedModel,
    //   defaultUsed: !postingPromptState.data?.modelId && !settings?.ai?.selectedModel
    // })
    return modelId
  })
  
  // Get all available AI models
  const allModels = $derived.by(() => {
    const models: AIModelInfo[] = []
    const providers = settings?.aiProviders || []
    
    // Debug: Uncomment to see provider details
    // console.log('[Toolbar] Computing allModels, providers:', providers)
    
    // Get enabled providers
    const enabledProviders = providers.filter(p => p.enabled)
    
    // Add models from each enabled provider
    for (const provider of enabledProviders) {
      const providerModels = AI_MODELS[provider.id] || []
      models.push(...providerModels)
    }
    
    // Always include OpenRouter models (they're free)
    const openrouterModels = AI_MODELS['openrouter'] || []
    if (!models.some(m => m.provider === 'openrouter')) {
      // console.log('[Toolbar] Adding OpenRouter models:', openrouterModels)
      models.push(...openrouterModels)
    }
    
    // console.log('[Toolbar] Total models:', models.length, models)
    return models
  })
  
  // Get current model
  const currentModel = $derived(
    allModels.find(m => m.id === selectedModelId) || 
    // Fallback to a default model object if not found
    (selectedModelId === 'openai/gpt-oss-120b' ? {
      id: 'openai/gpt-oss-120b',
      name: 'GPT-OSS 120B',
      provider: 'openrouter',
      icon: openrouterIcon,
      description: 'Free open-source model via OpenRouter',
      maxTokens: 128000,
      recommended: true
    } : allModels[0])
  )
  
  // Sort models with recommended first
  function sortedModels() {
    return [...allModels].sort((a, b) => {
      if (a.recommended && !b.recommended) return -1
      if (!a.recommended && b.recommended) return 1
      return 0
    })
  }
  
  // Get provider icon based on model provider
  function getProviderIcon(modelId: string): string | null {
    if (!modelId) return null
    
    // Find the model and check its provider
    const model = allModels.find(m => m.id === modelId)
    
    // If model not found in allModels, check if it's a known OpenRouter model
    if (!model) {
      // Default OpenRouter models
      if (modelId === 'openai/gpt-oss-120b' || modelId === 'openai/qwen3-32b') {
        return openrouterIcon
      }
      return null
    }
    
    // Return icon path based on actual provider
    switch (model.provider) {
      case 'openai': return openaiIcon
      case 'anthropic': return claudeIcon
      case 'google': return googleIcon
      case 'upstage': return upstageIcon
      case 'openrouter': return openrouterIcon
      default: return null
    }
  }
  
  // Get abbreviated model name
  function getAbbreviatedModelName(model?: AIModelInfo): string {
    if (!model) return 'AI'
    const name = model.name
    
    // Common abbreviations
    if (name.includes('GPT-OSS')) return 'GPT-OSS'
    if (name.includes('GPT-4o Mini')) return 'GPT-4o Mini'
    if (name.includes('GPT-4o')) return 'GPT-4o'
    if (name.includes('Claude 3.5 Sonnet')) return 'Claude Sonnet'
    if (name.includes('Claude 3.5 Haiku')) return 'Claude Haiku'
    if (name.includes('Gemini Pro')) return 'Gemini Pro'
    if (name.includes('Gemini Flash')) return 'Gemini Flash'
    if (name.includes('Solar')) return 'Solar'
    
    // Fallback: take first two parts
    const parts = name.split(' ')
    return parts.slice(0, 2).join(' ')
  }
  
  function selectModel(model: AIModelInfo) {
    showModelDropdown = false
    
    // Update posting-specific model
    postingPromptStore.updateModel(model.id)
    
    // Also update global settings so the selection persists
    settingsActions.updateAISettings({ selectedModel: model.id })
    
    // Show status message
    const shortName = getAbbreviatedModelName(model)
    window.dispatchEvent(new CustomEvent('status-message', { 
      detail: { 
        message: `포스팅 AI 모델 변경: ${shortName}`, 
        type: 'success' 
      } 
    }))
  }
  
  function handleThemeChange(newTheme: ThemeMode) {
    currentTheme = newTheme
    settingsActions.updateEditor({ theme: newTheme })
  }
  
  function handleUndo() {
    const tab = get(activeTab)
    if (!tab) return
    const content = fileStore.undo(tab.id)
    if (content !== null) {
      window.dispatchEvent(new CustomEvent('editor:undo', { detail: { text: content } }))
    }
  }
  
  function handleRedo() {
    const tab = get(activeTab)
    if (!tab) return
    const content = fileStore.redo(tab.id)
    if (content !== null) {
      window.dispatchEvent(new CustomEvent('editor:redo', { detail: { text: content } }))
    }
  }
  
  
  function handleKeydown(event: KeyboardEvent) {
    // Check for Cmd+Z (undo) and Cmd+Shift+Z (redo) on Mac
    // or Ctrl+Z (undo) and Ctrl+Y or Ctrl+Shift+Z (redo) on Windows/Linux
    if ((event.ctrlKey || event.metaKey) && !event.altKey) {
      if (event.key === 'z' && !event.shiftKey) {
        event.preventDefault()
        handleUndo()
      } else if (event.key === 'z' && event.shiftKey) {
        event.preventDefault()
        handleRedo()
      } else if (event.key === 'y' && !event.shiftKey) {
        // Keep Ctrl+Y for Windows/Linux compatibility
        event.preventDefault()
        handleRedo()
      }
    }
  }
  
  onMount(() => {
    
    // Initialize theme from settings
    const initSettings = settingsStore.get()
    
    currentTheme = (initSettings.editor?.theme || 'auto') as ThemeMode
    
    // Check if selected model requires API key that we don't have
    const selectedModel = initSettings.ai?.selectedModel
    let shouldResetToDefault = false
    
    if (!selectedModel) {
      shouldResetToDefault = true
    } else if (selectedModel.startsWith('gpt-') || selectedModel.startsWith('o1-')) {
      // OpenAI models - check for API key
      const openaiProvider = initSettings.aiProviders?.find(p => p.id === 'openai')
      if (!openaiProvider?.apiKey || openaiProvider.apiKey.trim() === '') {
        shouldResetToDefault = true
      }
    } else if (selectedModel.startsWith('claude-')) {
      // Anthropic models - check for API key
      const anthropicProvider = initSettings.aiProviders?.find(p => p.id === 'anthropic')
      if (!anthropicProvider?.apiKey || anthropicProvider.apiKey.trim() === '') {
        shouldResetToDefault = true
      }
    } else if (selectedModel.startsWith('gemini-')) {
      // Google models - check for API key
      const googleProvider = initSettings.aiProviders?.find(p => p.id === 'google')
      if (!googleProvider?.apiKey || googleProvider.apiKey.trim() === '') {
        shouldResetToDefault = true
      }
    }

    if (shouldResetToDefault) {
      // Set default AI model to OpenRouter's free model
      settingsActions.updateAISettings({
        selectedModel: 'openai/gpt-oss-120b',
        enabled: true,
        provider: 'openrouter'
      })
    }
    
    // Add keyboard shortcuts
    window.addEventListener('keydown', handleKeydown)
  })
  
  // Handle click outside for model dropdown
  $effect(() => {
    if (showModelDropdown) {
      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as Node
        if (modelDropdownRef && !modelDropdownRef.contains(target) && 
            modelButtonRef && !modelButtonRef.contains(target)) {
          showModelDropdown = false
        }
      }
      
      // Recalculate position on window resize or layout changes
      const handleReposition = () => {
        if (modelButtonRef && modelDropdownRef && showModelDropdown) {
          const buttonRect = modelButtonRef.getBoundingClientRect()
          const dropdownEl = modelDropdownRef.querySelector('.toolbar-model-dropdown')
          
          if (dropdownEl) {
            // Get actual dropdown height
            const dropdownRect = dropdownEl.getBoundingClientRect()
            const dropdownHeight = dropdownRect.height || 100
            
            // Calculate button center
            const buttonCenter = buttonRect.top + (buttonRect.height / 2)
            
            // Center dropdown with button, but ensure it stays within viewport
            let topPosition = buttonCenter - (dropdownHeight / 2)
            
            // Ensure dropdown doesn't go above viewport
            if (topPosition < 10) {
              topPosition = 10
            }
            
            // Ensure dropdown doesn't go below viewport
            const viewportHeight = window.innerHeight
            if (topPosition + dropdownHeight > viewportHeight - 10) {
              topPosition = viewportHeight - dropdownHeight - 10
            }
            
            dropdownPosition = {
              left: buttonRect.right + 8,
              top: topPosition
            }
          }
        }
      }
      
      // Add delay to prevent immediate close on open
      const timer = setTimeout(() => {
        document.addEventListener('click', handleClickOutside)
      }, 100)
      
      // Listen for resize events and transitionend events
      window.addEventListener('resize', handleReposition)
      document.addEventListener('transitionend', handleReposition)
      
      // Initial position calculation
      handleReposition()
      
      return () => {
        clearTimeout(timer)
        document.removeEventListener('click', handleClickOutside)
        window.removeEventListener('resize', handleReposition)
        document.removeEventListener('transitionend', handleReposition)
      }
    }
    return undefined
  })
  
  onDestroy(() => {
    window.removeEventListener('keydown', handleKeydown)
    if (unsubscribeCanUndo) unsubscribeCanUndo()
    if (unsubscribeCanRedo) unsubscribeCanRedo()
  })

  // Check if mobile
  let isMobile = $state(false)

  $effect(() => {
    if (typeof window !== 'undefined') {
      isMobile = window.innerWidth < 768

      const handleResize = () => {
        isMobile = window.innerWidth < 768
      }

      window.addEventListener('resize', handleResize)

      return () => {
        window.removeEventListener('resize', handleResize)
      }
    }
    return undefined
  })
</script>

<div class="toolbar">
  <!-- AI Features Section (moved to top) -->
  <!-- Prompt (프롬프트) - moved to first position -->
  <button
    class="toolbar-btn ai-feature"
    onclick={onPrompt}
    disabled={!isFileOpen || isInSplitView || hasGrammarCheckErrors}
    title={!isFileOpen ? "파일을 먼저 열어주세요" : isInSplitView ? "분할 편집 모드에서는 사용할 수 없습니다" : hasGrammarCheckErrors ? "맞춤법 검사 결과가 있을 때는 사용할 수 없습니다" : "프롬프트로 편집 (⌘K)"}
    aria-label="프롬프트"
  >
    <MessageSquare size={16} />
    <span class="ai-badge">AI</span>
  </button>
  
  <!-- Continue (이어쓰기) -->
  <button
    class="toolbar-btn ai-feature"
    onclick={() => onContinue?.()}
    disabled={!isFileOpen || isInSplitView || hasGrammarCheckErrors || (!editorText && !hasSelection)}
    title={!isFileOpen ? "파일을 먼저 열어주세요" : isInSplitView ? "분할 편집 모드에서는 사용할 수 없습니다" : hasGrammarCheckErrors ? "맞춤법 검사 결과가 있을 때는 사용할 수 없습니다" : !editorText && !hasSelection ? "텍스트를 입력하세요" : "이어쓰기 (Shift+Tab)"}
    aria-label="이어쓰기"
  >
    <PenLine size={16} />
    <span class="ai-badge">AI</span>
  </button>
  
  <!-- Paraphrase (개선) -->
  <button
    class="toolbar-btn ai-feature"
    onclick={onParaphrase}
    disabled={!isFileOpen || isInSplitView || hasGrammarCheckErrors || (!editorText && !hasSelection)}
    title={!isFileOpen ? "파일을 먼저 열어주세요" : isInSplitView ? "분할 편집 모드에서는 사용할 수 없습니다" : hasGrammarCheckErrors ? "맞춤법 검사 결과가 있을 때는 사용할 수 없습니다" : "개선 및 변경하기 (⌘⇧P)"}
    aria-label="개선 및 변경하기"
  >
    <RefreshCw size={16} />
    <span class="ai-badge">AI</span>
  </button>
  
  <!-- Expand (늘리기) -->
  <button
    class="toolbar-btn ai-feature"
    onclick={onExpand}
    disabled={!isFileOpen || isInSplitView || hasGrammarCheckErrors || (!editorText && !hasSelection)}
    title={!isFileOpen ? "파일을 먼저 열어주세요" : isInSplitView ? "분할 편집 모드에서는 사용할 수 없습니다" : hasGrammarCheckErrors ? "맞춤법 검사 결과가 있을 때는 사용할 수 없습니다" : "내용 늘리기 (⌘⇧E)"}
    aria-label="내용 늘리기"
  >
    <Maximize2 size={16} />
    <span class="ai-badge">AI</span>
  </button>
  
  <!-- Shrink (줄이기) -->
  <button
    class="toolbar-btn ai-feature"
    onclick={onShrink}
    disabled={!isFileOpen || isInSplitView || hasGrammarCheckErrors || (!editorText && !hasSelection)}
    title={!isFileOpen ? "파일을 먼저 열어주세요" : isInSplitView ? "분할 편집 모드에서는 사용할 수 없습니다" : hasGrammarCheckErrors ? "맞춤법 검사 결과가 있을 때는 사용할 수 없습니다" : "내용 줄이기 (⌘⇧S)"}
    aria-label="내용 줄이기"
  >
    <Minimize2 size={16} />
    <span class="ai-badge">AI</span>
  </button>
  
  <!-- Summarize (요약하기) -->
  <button
    class="toolbar-btn ai-feature"
    onclick={onSummarize}
    disabled={!isFileOpen || isInSplitView || hasGrammarCheckErrors || (!hasSelection && !editorText)}
    title={!isFileOpen ? "파일을 먼저 열어주세요" : isInSplitView ? "분할 편집 모드에서는 사용할 수 없습니다" : hasGrammarCheckErrors ? "맞춤법 검사 결과가 있을 때는 사용할 수 없습니다" : hasSelection ? "선택 영역 요약하기 (⌘⇧M)" : "전체 텍스트 요약하기 (⌘⇧M)"}
    aria-label="요약하기"
  >
    <ListFilter size={16} />
    <span class="ai-badge">AI</span>
  </button>
  
  <!-- Critic (리뷰) - Commented out for now -->
  <!--
  <button
    class="toolbar-btn ai-feature"
    onclick={onCritic}
    disabled={true}
    title="평가받기 (⌘⇧K) - 준비중"
    aria-label="평가받기"
  >
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5">
      <path d="M8 2C4 2 2 5 2 8c0 2 1 4 2 5l1 3 3-2c0.5 0 1 0 1.5 0C12 14 14 11 14 8c0-3-2-6-6-6z"/>
      <circle cx="5.5" cy="8" r="0.5" fill="currentColor"/>
      <circle cx="8" cy="8" r="0.5" fill="currentColor"/>
      <circle cx="10.5" cy="8" r="0.5" fill="currentColor"/>
    </svg>
    <span class="ai-badge">AI</span>
  </button>
  -->
  
  <div class="toolbar-separator"></div>
  
  <!-- AI Model & System Prompt Section -->
  <!-- AI Model Selector -->
  <div class="toolbar-model-selector" bind:this={modelDropdownRef}>
    <button
      class="toolbar-btn toolbar-btn-model ai-feature"
      bind:this={modelButtonRef}
      onclick={(e) => {
        e.stopPropagation()
        if (!showModelDropdown && modelButtonRef) {
          showModelDropdown = true
          // Re-calculate position after dropdown is rendered
          requestAnimationFrame(() => {
            if (modelButtonRef && modelDropdownRef) {
              const buttonRect = modelButtonRef.getBoundingClientRect()
              const dropdownEl = modelDropdownRef.querySelector('.toolbar-model-dropdown')
              
              if (dropdownEl) {
                // Get actual dropdown height
                const dropdownRect = dropdownEl.getBoundingClientRect()
                const dropdownHeight = dropdownRect.height || 100 // fallback height
                
                // Calculate button center
                const buttonCenter = buttonRect.top + (buttonRect.height / 2)
                
                // Center dropdown with button, but ensure it stays within viewport
                let topPosition = buttonCenter - (dropdownHeight / 2)
                
                // Ensure dropdown doesn't go above viewport
                if (topPosition < 10) {
                  topPosition = 10
                }
                
                // Ensure dropdown doesn't go below viewport
                const viewportHeight = window.innerHeight
                if (topPosition + dropdownHeight > viewportHeight - 10) {
                  topPosition = viewportHeight - dropdownHeight - 10
                }
                
                dropdownPosition = {
                  left: buttonRect.right + 8,
                  top: topPosition
                }
              }
            }
          })
        } else {
          showModelDropdown = false
        }
      }}
      title={`AI 모델: ${getAbbreviatedModelName(currentModel)}`}
      aria-label="AI 모델 선택"
    >
      {#if getProviderIcon(selectedModelId)}
        <img 
          src={getProviderIcon(selectedModelId)} 
          alt="{currentModel?.provider} logo" 
          width="16" 
          height="16"
          class="provider-logo"
          style="object-fit: contain;"
        />
      {:else}
        <!-- Default robot icon when no provider icon available -->
        <Bot size={16} />
      {/if}
      <span class="ai-badge">AI</span>
    </button>
    
    <!-- Model Dropdown -->
    {#if showModelDropdown}
      <div class="toolbar-model-dropdown" style="display: block; left: {dropdownPosition.left}px; top: {dropdownPosition.top}px; visibility: {dropdownPosition.top > 0 ? 'visible' : 'hidden'};">
        {#if allModels.length === 0}
          <div style="padding: 10px; color: #666;">모델을 불러오는 중...</div>
        {:else}
        <div class="model-dropdown-list">
          {#each sortedModels() as model}
            <button
              class="model-dropdown-item"
              class:selected={selectedModelId === model.id}
              onclick={() => selectModel(model)}
            >
              <div class="model-item-info">
                <div class="model-item-name">
                  {model.name}
                  {#if model.recommended}
                    <span class="model-recommended-badge">추천</span>
                  {/if}
                </div>
              </div>
              {#if selectedModelId === model.id}
                <svg class="model-check-icon" width="10" height="10" viewBox="0 0 14 14">
                  <path 
                    d="M2 7l3 3 7-7" 
                    stroke="#6366f1" 
                    fill="none" 
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              {/if}
            </button>
          {/each}
        </div>
        {/if}
      </div>
    {/if}
  </div>
  
  <!-- Unified Prompt (통합 프롬프트) -->
  <button
    class="toolbar-btn ai-feature"
    class:active={postingPromptStore.hasPrompt() || systemPromptStore.hasPrompt()}
    onclick={onUnifiedPrompt}
    title="프롬프트 설정"
    aria-label="프롬프트"
  >
    <SlidersHorizontal size={16} />
    <span class="ai-badge">AI</span>
  </button>
  
  <div class="toolbar-separator"></div>
  
  <!-- Editing Tools Section -->
  <!-- Undo -->
  <button
    class="toolbar-btn"
    onclick={handleUndo}
    disabled={!isFileOpen || !canUndoState}
    title={!isFileOpen ? "파일을 먼저 열어주세요" : "실행 취소 (⌘+Z)"}
    aria-label="실행 취소"
  >
    <Undo2 size={16} />
  </button>
  
  <!-- Redo -->
  <button
    class="toolbar-btn"
    onclick={handleRedo}
    disabled={!isFileOpen || !canRedoState}
    title={!isFileOpen ? "파일을 먼저 열어주세요" : "다시 실행 (⌘+Shift+Z)"}
    aria-label="다시 실행"
  >
    <Redo2 size={16} />
  </button>
  
  <div class="toolbar-separator"></div>
  
  <!-- Cut -->
  <button
    class="toolbar-btn"
    onclick={onCut}
    disabled={!isFileOpen || isInSplitView || !editorText}
    title={!isFileOpen ? "파일을 먼저 열어주세요" : isInSplitView ? "분할 편집 모드에서는 사용할 수 없습니다" : "잘라내기 (⌘+X)"}
    aria-label="잘라내기"
  >
    <Scissors size={16} />
  </button>
  
  <!-- Copy -->
  <button
    class="toolbar-btn"
    onclick={onCopy}
    disabled={!isFileOpen || isInSplitView || !editorText}
    title={!isFileOpen ? "파일을 먼저 열어주세요" : isInSplitView ? "분할 편집 모드에서는 사용할 수 없습니다" : "복사 (⌘+C)"}
    aria-label="복사"
  >
    <Copy size={16} />
  </button>
  
  <!-- Paste -->
  <button
    class="toolbar-btn"
    onclick={onPaste}
    disabled={!isFileOpen || isInSplitView}
    title={!isFileOpen ? "파일을 먼저 열어주세요" : isInSplitView ? "분할 편집 모드에서는 사용할 수 없습니다" : "붙여넣기 (⌘+V)"}
    aria-label="붙여넣기"
  >
    <ClipboardPaste size={16} />
  </button>
  
  <!-- Clear -->
  <button
    class="toolbar-btn"
    onclick={onClear}
    disabled={!isFileOpen || isInSplitView || !editorText}
    title={!isFileOpen ? "파일을 먼저 열어주세요" : isInSplitView ? "분할 편집 모드에서는 사용할 수 없습니다" : "지우기"}
    aria-label="지우기"
  >
    <Trash2 size={16} />
  </button>
  
  <div class="toolbar-separator"></div>
  
  <!-- PDF Export -->
  <button
    class="toolbar-btn"
    onclick={onExportPDF}
    disabled={!isFileOpen || !editorText}
    title={!isFileOpen ? "파일을 먼저 열어주세요" : "PDF로 내보내기"}
    aria-label="PDF 내보내기"
  >
    <FileDown size={16} />
  </button>
  
  <!-- Spacer to push settings to bottom -->
  <div class="toolbar-spacer"></div>
  
  <div class="toolbar-separator"></div>
  
  <!-- Dev Mode Indicator (hide in demo mode) -->
  {#if isDev && !isDemo}
    <div class="toolbar-dev-indicator" title="Development Mode">
      <span class="dev-dot"></span>
      <span class="dev-text">DEV</span>
    </div>
  {/if}
  
  <!-- Bottom section with consistent spacing -->
  <div class="toolbar-bottom-section">
    <!-- Fullscreen Toggle / Open in New Tab (mobile) -->
    <button
      class="toolbar-btn"
      onclick={onToggleFullscreen}
      title={isMobile ? "새 탭에서 열기" : isFullscreen ? "전체화면 종료 (F11)" : "전체화면 (F11)"}
      aria-label={isMobile ? "새 탭에서 열기" : isFullscreen ? "전체화면 종료" : "전체화면"}
    >
      {#if isMobile}
        <ExternalLink size={16} />
      {:else if isFullscreen}
        <Minimize size={16} />
      {:else}
        <Maximize size={16} />
      {/if}
    </button>
    
    <!-- Settings -->
    <button
      class="toolbar-btn"
      onclick={onOpenSettings}
      title="설정 (⌘+,)"
      aria-label="설정"
    >
      <Settings size={16} />
    </button>
    
    <!-- Theme Toggle -->
    <ThemeToggleLogo 
      {currentTheme}
      onThemeChange={handleThemeChange}
    />
  </div>
</div>

<style>
  .toolbar {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 8px 4px;
    background: var(--bg-primary);
    border-right: 1px solid var(--border-primary);
    gap: 2px;
    width: 40px;
    height: 100%;
    overflow: visible;
    position: relative;
  }
  
  .toolbar-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.15s ease;
    position: relative;
  }
  
  .toolbar-btn:hover:not(:disabled) {
    background: var(--bg-hover, rgba(0, 0, 0, 0.05));
    color: var(--text-primary);
  }
  
  .toolbar-btn:active:not(:disabled) {
    background: var(--bg-active, rgba(0, 0, 0, 0.08));
    transform: scale(0.98);
  }
  
  .toolbar-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  
  .toolbar-btn :global(svg) {
    width: 16px;
    height: 16px;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  
  .toolbar-separator {
    width: 16px;
    height: 1px;
    background: var(--border-secondary);
    margin: 4px 0;
    opacity: 0.5;
  }
  
  /* AI Features styling */
  .toolbar-btn.ai-feature {
    position: relative;
  }
  
  .toolbar-btn.active {
    background: var(--bg-active, rgba(99, 102, 241, 0.1));
    color: var(--accent-primary);
  }
  
  /* AI Badge styling */
  .ai-badge {
    position: absolute;
    bottom: 1px;
    right: 1px;
    font-size: 7px;
    font-weight: 600;
    letter-spacing: 0.3px;
    background: var(--primary, #4f46e5);
    color: white;
    padding: 0 2px;
    border-radius: 2px;
    line-height: 1;
    height: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.85;
    pointer-events: none;
  }
  
  .toolbar-btn:hover .ai-badge {
    opacity: 1;
  }
  
  .toolbar-btn:disabled .ai-badge {
    opacity: 0.3;
  }
  
  /* Dark mode AI badge */
  :global(:root.dark-theme) .ai-badge {
    background: var(--primary, #6366f1);
  }
  
  .toolbar-spacer {
    flex: 1;
  }
  
  /* Bottom section with consistent spacing */
  .toolbar-bottom-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
  }
  
  /* Dev Mode Indicator */
  .toolbar-dev-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 3px;
    padding: 4px 0;
    margin-bottom: 4px;
  }
  
  .dev-dot {
    width: 5px;
    height: 5px;
    background: #ff8c00;
    border-radius: 50%;
    animation: pulse 2s infinite;
  }
  
  .dev-text {
    font-size: 9px;
    font-weight: 600;
    color: #ff8c00;
    letter-spacing: 0.5px;
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
  
  /* AI Model Selector */
  .toolbar-model-selector {
    position: relative;
    margin-bottom: 4px;
    z-index: 10000;
  }
  
  .toolbar-btn-model {
    position: relative;
  }
  
  .toolbar-btn-model img {
    display: block;
    image-rendering: crisp-edges;
    image-rendering: -webkit-optimize-contrast;
  }
  
  /* Apply filter to provider logos */
  .provider-logo {
    transition: filter 0.2s;
  }
  
  .toolbar-model-dropdown {
    position: fixed;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    min-width: 180px;
    max-width: 220px;
    max-height: 300px;
    z-index: 99999;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  
  .model-dropdown-list {
    flex: 1;
    overflow-y: auto;
    padding: 3px;
    max-height: 250px;
  }
  
  /* Hide scrollbar but keep functionality */
  .model-dropdown-list::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
  
  .model-dropdown-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 5px 6px;
    width: 100%;
    background: transparent;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.15s;
    text-align: left;
  }
  
  .model-dropdown-item:hover {
    background: #f3f4f6;
  }
  
  .model-dropdown-item.selected {
    background: #ede9fe;
  }
  
  .model-item-info {
    flex: 1;
    min-width: 0;
  }
  
  .model-item-name {
    font-size: 11px;
    font-weight: 500;
    color: #374151;
    display: flex;
    align-items: center;
    gap: 3px;
  }
  
  
  .model-recommended-badge {
    font-size: 8px;
    padding: 0px 3px;
    background: #fef3c7;
    color: #92400e;
    border-radius: 3px;
    font-weight: 600;
  }
  
  .model-check-icon {
    flex-shrink: 0;
    margin-left: 6px;
  }
  
  /* Dark mode */
  :global(:root.dark-theme) .toolbar {
    background: var(--bg-secondary);
    border-color: var(--border-primary);
  }
  
  :global(:root.dark-theme) .toolbar-btn {
    color: var(--text-tertiary);
  }
  
  :global(:root.dark-theme) .toolbar-btn:hover:not(:disabled) {
    background: var(--bg-hover, rgba(255, 255, 255, 0.05));
    color: var(--text-primary);
  }
  
  :global(:root.dark-theme) .toolbar-btn:active:not(:disabled) {
    background: var(--bg-active, rgba(255, 255, 255, 0.08));
  }
  
  :global(:root.dark-theme) .toolbar-model-dropdown {
    background: var(--bg-secondary);
    border-color: var(--border-primary);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
  }
  
  :global(:root.dark-theme) .model-dropdown-item:hover {
    background: var(--bg-tertiary);
  }
  
  :global(:root.dark-theme) .model-dropdown-item.selected {
    background: rgba(124, 122, 255, 0.15);
  }
  
  :global(:root.dark-theme) .model-item-name {
    color: var(--text-primary);
  }
  
  
  :global(:root.dark-theme) .model-recommended-badge {
    background: rgba(254, 243, 199, 0.2);
    color: #fbbf24;
  }
  
  /* Apply white filter to provider logos in dark theme */
  :global(:root.dark-theme) .provider-logo {
    filter: brightness(0) saturate(100%) invert(100%);
  }

  /* Auto theme: follow system preference */
  @media (prefers-color-scheme: dark) {
    :root:not(.light-theme) .provider-logo {
      filter: brightness(0) saturate(100%) invert(100%);
    }
  }
  
  /* Keyboard focus */
  .toolbar-btn:focus-visible {
    outline: 2px solid var(--accent-primary);
    outline-offset: 1px;
  }
  
  /* Tooltip-like hint on long hover */
  @media (hover: hover) {
    .toolbar-btn:hover::after {
      content: attr(title);
      position: absolute;
      left: 100%;
      margin-left: 8px;
      padding: 4px 8px;
      background: rgba(0, 0, 0, 0.8);
      color: white;
      font-size: 11px;
      border-radius: 4px;
      white-space: nowrap;
      pointer-events: none;
      opacity: 0;
      animation: tooltip-fade-in 0.2s ease 0.5s forwards;
      z-index: 9999;
    }
  }
  
  @keyframes tooltip-fade-in {
    to {
      opacity: 1;
    }
  }
  
  /* Auto theme (system dark mode) support */
  @media (prefers-color-scheme: dark) {
    .toolbar-model-dropdown {
      background: var(--bg-secondary);
      border-color: var(--border-primary);
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
    }

    .model-dropdown-item:hover {
      background: var(--bg-tertiary);
    }

    .model-dropdown-item.selected {
      background: rgba(124, 122, 255, 0.15);
    }

    .model-item-name {
      color: var(--text-primary);
    }
  }
</style>